// Institute of Design and Innovation (email idi@unm.edu). //

import Appointment from '@/models/appointment'
import { AppointmentTypeDetail } from '@/models/appointmentType'
import User from '@/models/user'
import UserReassign from '@/models/userReassign'
import { AxiosResponse } from 'axios'
import httpClient from './httpClient'

/**
 * Class containing routes for AppointmentService API
 */
export default class AppointmentService {
  static cancelAppointment (id: number): Promise<AxiosResponse<Appointment>> {
    return httpClient.put(`appointment/${id}/cancel`)
  }

  /**
   * Get all appointments for range
   * @param start start date and time
   * @param end end date and time
   */
  static getAll (start?: Date, end?: Date): Promise<AxiosResponse<Array<Appointment>>> {
    const params = {} as any

    if (start) {
      params.start = start.toISOString()
    }

    if (end) {
      params.end = end.toISOString()
    }

    return httpClient.get('appointment', { params: params })
  }

  /**
   * Get the appointment
   * @param id the appointment Id
   */
  static get (id: number): Promise<AxiosResponse<Appointment>> {
    return httpClient.get(`appointment/${id}`)
  }

  /**
   * Get appointments for project user
   * @param userId the user Id
   * @param onlyFuture optional flag if only want future appoitnments
   */
  static getForProjectUser (userId: number, onlyFuture = true): Promise<AxiosResponse<Array<Appointment>>> {
    return httpClient.get(`appointment/forProjectUser/${userId}`, { params: { onlyFuture: onlyFuture } })
  }

  /**
   * Reassign the appointment for the reviewer
   * @param appointmentId the appointment Id
   * @param reviewerId the reviewer user Id
   */
  static reassign (appointmentId: number, reviewerId: number): Promise<AxiosResponse<Appointment>> {
    return httpClient.put(`appointment/${appointmentId}/reassign/${reviewerId}`)
  }

  /**
   * Get available reviewers for the date
   * @param date the date
   */
  static reviewerAvailabilityForDate (date: Date): Promise<AxiosResponse<Array<UserReassign>>> {
    return httpClient.get('appointment/reviewerAvailabilityForDate', { params: { date: date.toISOString() } })
  }

  /**
   * Post the appointment
   * @param form the appointment form
   */
  static scheduleAppointment (form: object): Promise<AxiosResponse<Appointment>> {
    return httpClient.post('appointment', form)
  }

  /**
   * Put a reschedule appointment
   * @param id the appointment Id
   * @param form the new appointment form
   */
  static rescheduleAppointment (id: number, form: object, scheduledByOsp = true): Promise<AxiosResponse<Appointment>> {
    return httpClient.put(`appointment/${id}/reschedule`, form, { params: { scheduledByOsp: scheduledByOsp } })
  }

  static getIntakeForm (id: number): Promise<AxiosResponse<any>> {
    return httpClient.get(`appointment/${id}/intakeForm`)
  }

  static updateIntakeForm (id: number, form: object): Promise<AxiosResponse<Appointment>> {
    return httpClient.put(`appointment/${id}/intakeForm`, form)
  }

  static reviewerAvailability (offeringId: number, dateTime: Date): Promise<AxiosResponse<Array<UserReassign>>> {
    return httpClient.get(`appointment/reviewerAvailability/${offeringId}/${dateTime.toISOString()}`)
  }

  /**
   * The display value for the proposal number formatted to include the
   * proposal number type
   */
  static formatProposalNumber (appointment: Appointment): string {
    let type: string
    switch (appointment.proposalNumberType) {
      case 'ProposalDevelopment': type = 'Proposal Development'; break
      case 'QuickProposal': type = 'Quick Proposal'; break
      default: type = 'Cayuse'
    }

    return `${type}: ${appointment.proposalNumber}`
  }
}
